import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Zap, Shield, ChevronRight, CheckCircle, Play, AlertTriangle, RotateCcw, Pencil, FileText, Upload, Calendar, ChevronDown, ChevronUp, Plus, Trash2, X, Archive, Flag } from 'lucide-react';
import { format } from 'date-fns';
import ArchiveWarningDialog from '@/components/common/ArchiveWarningDialog';
import MissionClosureDialog from '@/components/missions/MissionClosureDialog';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MeasurementCombobox, PositionCombobox, PositionValueCombobox, FormattedNumberInput, TIER_FILTERS } from "@/components/bullseye/BullseyeFormComponents";

export default function MissionControl() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedTacticalPriorities, setSelectedTacticalPriorities] = useState([]);
  const [selectedSponsors, setSelectedSponsors] = useState([]);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [missionToArchive, setMissionToArchive] = useState(null);
  const [archiveDependents, setArchiveDependents] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [closingMission, setClosingMission] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const { data: missions, isLoading } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: officers } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: strategicNodes } = useQuery({
    queryKey: ['strategicNodes'],
    queryFn: () => base44.entities.StrategicNode.list(),
    initialData: []
  });

  const { data: objectives } = useQuery({
    queryKey: ['objectives'],
    queryFn: () => base44.entities.Objective.list(),
    initialData: []
  });

  const { data: activities } = useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.list(),
    initialData: []
  });

  const { data: dailyTasks } = useQuery({
    queryKey: ['dailyTasks'],
    queryFn: () => base44.entities.DailyTask.list(),
    initialData: []
  });

  const { data: vocabRecords } = useQuery({
    queryKey: ['bullseyeVocabulary'],
    queryFn: () => base44.entities.BullseyeVocabulary.list(),
    initialData: []
  });

  const measurements = (vocabRecords || []).filter(v => v.type === 'measurement' && v.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const positions = (vocabRecords || []).filter(v => v.type === 'position' && v.is_active).sort((a, b) => a.sort_order - b.sort_order);
  const measurementCategories = [...new Set(measurements.map(m => m.category))];
  const positionCategories = [...new Set(positions.map(p => p.category))];

  const [positionSuggestions, setPositionSuggestions] = useState([]);

  const { data: user } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: currentProfile } = useQuery({
    queryKey: ['currentProfile', user?.email],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === user?.email);
    },
    enabled: !!user?.email
  });

  const isCommander = currentProfile?.custom_role === 'Commander' || currentProfile?.custom_role === 'Commander in Chief';

  const createMissionMutation = useMutation({
    mutationFn: async (data) => {
      // Generate mission code - find highest existing code number to prevent duplicates
      const prefix = data.game_mode === 'rank_and_file' ? 'RF' :
      data.game_mode === 'campaign' ? 'C' : 'SO';
      const sameModeRegex = new RegExp(`^${prefix}(\\d+)$`);
      const existingNumbers = missions.
      filter((m) => m.game_mode === data.game_mode && m.mission_code).
      map((m) => {
        const match = m.mission_code.match(sameModeRegex);
        return match ? parseInt(match[1], 10) : 0;
      }).
      filter((n) => !isNaN(n));

      const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
      const mission_code = `${prefix}${nextNumber}`;

      return base44.entities.Mission.create({ ...data, mission_code });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missions'] });
      setNewMission({ title: '', objective: '', game_mode: 'rank_and_file', xp_reward: 100, sponsor_email: '', owner_email: '', affiliated_officers: [], commanders_intent: '', linked_priorities: [], briefing_text: '', briefing_files: [], briefing_events: [], status_flag: 'yellow', fibery_url: '', target_measurement_id: '', target_value: '', target_unit: '', target_position_type: '', target_position_value: '', target_deadline: '' });
      setIsDialogOpen(false);
    }
  });

  const updateMissionStatusFlagMutation = useMutation({
    mutationFn: ({ id, status_flag }) => base44.entities.Mission.update(id, { status_flag }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] })
  });

  const reactivateMissionMutation = useMutation({
    mutationFn: (missionId) => base44.entities.Mission.update(missionId, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] })
  });

  const updateMissionMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const originalMission = missions.find(m => m.id === id);
      let finalData = { ...data };
      
      // If game mode changed OR mission_code prefix is inconsistent, regenerate mission code
      const expectedPrefix = data.game_mode === 'rank_and_file' ? 'RF' :
                             data.game_mode === 'campaign' ? 'C' : 'SO';
      const currentPrefix = originalMission?.mission_code?.match(/^([A-Z]+)/)?.[1];

      if (originalMission && data.game_mode && (data.game_mode !== originalMission.game_mode || currentPrefix !== expectedPrefix)) {
        const prefix = expectedPrefix;
        const sameModeRegex = new RegExp(`^${prefix}(\\d+)$`);
        const existingNumbers = missions
          .filter((m) => m.game_mode === data.game_mode && m.mission_code)
          .map((m) => {
            const match = m.mission_code.match(sameModeRegex);
            return match ? parseInt(match[1], 10) : 0;
          })
          .filter((n) => !isNaN(n));

        const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
        finalData.mission_code = `${prefix}${nextNumber}`;
      }
      
      return base44.entities.Mission.update(id, finalData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missions'] });
      setNewMission({ title: '', objective: '', game_mode: 'rank_and_file', xp_reward: 100, sponsor_email: '', owner_email: '', affiliated_officers: [], commanders_intent: '', linked_priorities: [], briefing_text: '', briefing_files: [], briefing_events: [], status_flag: 'yellow', fibery_url: '', target_measurement_id: '', target_value: '', target_unit: '', target_position_type: '', target_position_value: '', target_deadline: '' });
      setEditingMission(null);
      setIsDialogOpen(false);
    }
  });

  const deleteMissionMutation = useMutation({
    mutationFn: async (id) => {
      const objectives = await base44.entities.Objective.list();
      const missionObjectives = objectives.filter((o) => o.mission_id === id);
      if (missionObjectives.length > 0) {
        throw new Error('Cannot delete mission with existing objectives');
      }
      return base44.entities.Mission.delete(id);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] })
  });

  const archiveMissionMutation = useMutation({
    mutationFn: (id) => base44.entities.Mission.update(id, { status: 'archived' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] })
  });

  const unarchiveMissionMutation = useMutation({
    mutationFn: (id) => base44.entities.Mission.update(id, { status: 'active' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['missions'] })
  });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [newMission, setNewMission] = useState({
    title: '',
    objective: '',
    game_mode: 'rank_and_file',
    xp_reward: 100,
    sponsor_email: '',
    owner_email: '',
    affiliated_officers: [],
    commanders_intent: '',
    linked_priorities: [],
    briefing_text: '',
    briefing_files: [],
    briefing_events: [],
    status_flag: 'yellow',
    fibery_url: '',
    target_measurement_id: '',
    target_value: '',
    target_unit: '',
    target_position_type: '',
    target_position_value: '',
    target_deadline: ''
  });

  useEffect(() => {
    if (!newMission.target_position_type) { setPositionSuggestions([]); return; }
    base44.functions.invoke('bullseyePositionValues', { position_type: newMission.target_position_type })
      .then(res => { setPositionSuggestions(res?.values || res?.data?.values || []); })
      .catch(() => { setPositionSuggestions([]); });
  }, [newMission.target_position_type]);

  const [newEvent, setNewEvent] = useState({
    event_type: 'milestone',
    event_name: '',
    event_date: '',
    frequency: ''
  });

  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState('');

  const handleCreate = (e) => {
    e.preventDefault();
    if (editingMission) {
      updateMissionMutation.mutate({
        id: editingMission.id,
        data: newMission
      });
    } else {
      createMissionMutation.mutate({
        ...newMission,
        status: 'active'
      });
    }
  };

  const handleEdit = (mission) => {
    setEditingMission(mission);
    setNewMission({
      title: mission.title,
      objective: mission.objective,
      game_mode: mission.game_mode,
      xp_reward: mission.xp_reward,
      sponsor_email: mission.sponsor_email || '',
      owner_email: mission.owner_email || '',
      affiliated_officers: mission.affiliated_officers || [],
      commanders_intent: mission.commanders_intent || '',
      linked_priorities: mission.linked_priorities || [],
      briefing_text: mission.briefing_text || '',
      briefing_files: mission.briefing_files || [],
      briefing_events: mission.briefing_events || [],
      status_flag: mission.status_flag || 'yellow',
      fibery_url: mission.fibery_url || '',
      target_measurement_id: mission.target_measurement_id || '',
      target_value: mission.target_value || '',
      target_unit: mission.target_unit || '',
      target_position_type: mission.target_position_type || '',
      target_position_value: mission.target_position_value || '',
      target_deadline: mission.target_deadline || ''
    });
    setIsDialogOpen(true);
  };

  const addEvent = () => {
    if (!newEvent.event_name || !newEvent.event_date) return;
    setNewMission({
      ...newMission,
      briefing_events: [...(newMission.briefing_events || []), { ...newEvent }]
    });
    setNewEvent({
      event_type: 'milestone',
      event_name: '',
      event_date: '',
      frequency: ''
    });
  };

  const removeEvent = (index) => {
    setNewMission({
      ...newMission,
      briefing_events: (newMission.briefing_events || []).filter((_, i) => i !== index)
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setNewMission({
        ...newMission,
        briefing_files: [...(newMission.briefing_files || []), file_url]
      });
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveFile = (fileUrl) => {
    setNewMission({
      ...newMission,
      briefing_files: newMission.briefing_files.filter((f) => f !== fileUrl)
    });
  };

  const addPriority = () => {
    if (!selectedPriority) return;
    const current = newMission.linked_priorities || [];
    if (!current.includes(selectedPriority)) {
      setNewMission({
        ...newMission,
        linked_priorities: [...current, selectedPriority]
      });
    }
    setSelectedPriority('');
  };

  const removePriority = (nodeId) => {
    setNewMission({
      ...newMission,
      linked_priorities: (newMission.linked_priorities || []).filter((id) => id !== nodeId)
    });
  };

  const handleArchiveClick = async (mission) => {
    const missionObjectives = objectives.filter((o) => o.mission_id === mission.id);
    const deps = [];

    missionObjectives.forEach((obj) => {
      deps.push({
        type: 'Objective',
        name: obj.statement,
        code: obj.objective_code,
        page: 'discovery'
      });

      // Get activities for this objective
      const objectiveActivities = activities.filter((a) => a.objective_id === obj.id);
      objectiveActivities.forEach((act) => {
        deps.push({
          type: 'Activity',
          name: act.title,
          code: act.activity_code,
          page: 'activities'
        });

        // Get tasks for this activity
        const activityTasks = dailyTasks.filter((t) => t.activity_id === act.id);
        activityTasks.forEach((task) => {
          deps.push({
            type: 'Daily Task',
            name: task.task_description,
            code: format(new Date(task.date), 'MMM d, yyyy'),
            page: 'dailytasks'
          });
        });
      });
    });

    setArchiveDependents(deps);
    setMissionToArchive(mission);
    setArchiveDialogOpen(true);
  };

  const handleArchiveConfirm = () => {
    if (missionToArchive) {
      archiveMissionMutation.mutate(missionToArchive.id);
    }
  };

  const handleDeleteClick = async (mission) => {
    const missionObjectives = objectives.filter((o) => o.mission_id === mission.id);
    if (missionObjectives.length > 0) {
      alert(`Cannot delete mission with ${missionObjectives.length} existing objective(s). Please delete or archive objectives first.`);
      return;
    }
    
    if (deleteConfirm === mission.id) {
      deleteMissionMutation.mutate(mission.id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(mission.id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const getMissionsByMode = (mode) => missions.filter((m) => m.game_mode === mode);

  const GameModeCard = ({ mode, title, icon: Icon, color, description, count }) =>
  <Card className="glass-panel border-none relative overflow-hidden group hover:bg-slate-800/50 transition-colors cursor-pointer" onClick={() => setActiveTab(mode)}>
      <div className={`absolute top-0 right-0 p-24 rounded-full ${color.bg} opacity-5 -translate-y-8 translate-x-8 group-hover:scale-110 transition-transform`} />
      <CardContent className="p-6">
        <div className="flex justify-between items-start mb-4">
          <div className={`p-3 rounded-xl ${color.bg} ${color.text}`}>
            <Icon className="w-6 h-6" />
          </div>
          <Badge variant="outline" className="font-mono">{count} Active</Badge>
        </div>
        <h3 className="text-gray-700 mb-2 text-xl font-bold">{title}</h3>
        <p className="text-sm text-slate-400">{description}</p>
      </CardContent>
    </Card>;


  const MissionCard = ({ mission }) => {
    const isCompleted = mission.status === 'completed';
    const isArchived = mission.status === 'archived';
    const isSpecialOps = mission.game_mode === 'special_ops';
    const [showBriefing, setShowBriefing] = useState(false);
    
    // Check if current user is sponsor or owner
    const isCiC = currentProfile?.custom_role === 'Commander in Chief';
    const canManageMission = user?.email === mission.sponsor_email || user?.email === mission.owner_email || isCiC;
    const canCompleteMission = user?.email === mission.sponsor_email || user?.email === mission.owner_email || isCiC;

    const linkedNodes = (mission.linked_priorities || []).
    map((id) => strategicNodes.find((n) => n.id === id)).
    filter(Boolean);

    // Calculate objective and activity status distribution
    const missionObjectives = objectives.filter((o) => o.mission_id === mission.id && o.status === 'active');
    const missionActivities = activities.filter((a) => {
      const obj = objectives.find((o) => o.id === a.objective_id);
      return obj && obj.mission_id === mission.id && a.status === 'active';
    });

    const objStatusCounts = {
      green: missionObjectives.filter((o) => o.status_flag === 'green').length,
      yellow: missionObjectives.filter((o) => o.status_flag === 'yellow').length,
      red: missionObjectives.filter((o) => o.status_flag === 'red').length
    };

    const actStatusCounts = {
      green: missionActivities.filter((a) => a.key_results_status === 'green').length,
      yellow: missionActivities.filter((a) => a.key_results_status === 'yellow').length,
      red: missionActivities.filter((a) => a.key_results_status === 'red').length
    };

    const objTotal = missionObjectives.length;
    const actTotal = missionActivities.length;

    return (
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`
          relative overflow-hidden rounded-xl border p-6
          ${isSpecialOps ? 'bg-rose-950/10 border-rose-500/20' : 'bg-slate-900/50 border-slate-800'}
          ${isCompleted || isArchived ? 'opacity-60' : ''}
        `}>

        {isSpecialOps &&
        <div className="absolute top-0 right-0 px-3 py-1 bg-rose-500 text-white text-xs font-bold uppercase tracking-widest rounded-bl-xl">
            Priority Alpha
          </div>
        }
        
        <div className="flex justify-between items-start gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className={`
                uppercase text-[10px] tracking-wider
                ${mission.game_mode === 'campaign' ? 'text-indigo-400 border-indigo-400/30' : ''}
                ${mission.game_mode === 'rank_and_file' ? 'text-slate-400 border-slate-400/30' : ''}
                ${mission.game_mode === 'special_ops' ? 'text-rose-400 border-rose-400/30' : ''}
              `}>
                {mission.game_mode.replace(/_/g, ' ')}
              </Badge>
              {mission.fibery_url && (
                <a href={mission.fibery_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  <Badge className="bg-purple-500/20 text-purple-400 border-none hover:bg-purple-500/30 transition-colors">
                    Fibery Link
                  </Badge>
                </a>
              )}
              {isArchived &&
              <Badge className="bg-amber-500/20 text-amber-400 border-none">
                  Archived
                </Badge>
              }
              {mission.status === 'active' &&
              <div className="flex gap-1">
                  <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateMissionStatusFlagMutation.mutate({ id: mission.id, status_flag: 'green' });
                  }}
                  className={`p-1 rounded ${mission.status_flag === 'green' ? 'bg-emerald-500' : 'bg-emerald-500/30 hover:bg-emerald-500/50'}`}>

                    <Flag className="w-3 h-3 text-white" />
                  </button>
                  <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateMissionStatusFlagMutation.mutate({ id: mission.id, status_flag: 'yellow' });
                  }}
                  className={`p-1 rounded ${mission.status_flag === 'yellow' ? 'bg-amber-500' : 'bg-amber-500/30 hover:bg-amber-500/50'}`}>

                    <Flag className="w-3 h-3 text-white" />
                  </button>
                  <button
                  onClick={(e) => {
                    e.stopPropagation();
                    updateMissionStatusFlagMutation.mutate({ id: mission.id, status_flag: 'red' });
                  }}
                  className={`p-1 rounded ${mission.status_flag === 'red' ? 'bg-red-500' : 'bg-red-500/30 hover:bg-red-500/50'}`}>

                    <Flag className="w-3 h-3 text-white" />
                  </button>
                </div>
              }
            </div>
            <h3 className="text-lg font-bold text-slate-100">
              {mission.mission_code && <span className="text-indigo-400 mr-2">[{mission.mission_code}]</span>}
              {mission.title}
            </h3>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-amber-400 font-mono font-bold">+{mission.xp_reward} XP</div>
            {canManageMission && (
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(mission);
                  }}
                  className="h-7 w-7 text-slate-500 hover:text-slate-300 hover:bg-slate-800">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                {isArchived ?
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    unarchiveMissionMutation.mutate(mission.id);
                  }}
                  className="h-7 w-7 text-slate-500 hover:text-emerald-400 hover:bg-slate-800"
                  title="Unarchive">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </Button> :

                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleArchiveClick(mission);
                  }}
                  className="h-7 w-7 text-slate-500 hover:text-amber-400 hover:bg-slate-800"
                  title="Archive">
                    <Archive className="w-3.5 h-3.5" />
                  </Button>
                }
                <div className="relative">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(mission);
                    }}
                    className="h-7 w-7 text-slate-500 hover:text-rose-400 hover:bg-slate-800"
                    title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                  {deleteConfirm === mission.id && (
                    <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                      Click again to confirm
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="mt-4 text-slate-400 text-sm leading-relaxed">
          {mission.objective}
        </p>

        {/* Objective and Activity Status Bars */}
        {mission.status === 'active' && (objTotal > 0 || actTotal > 0) &&
        <div className="mt-4 space-y-2">
            {objTotal > 0 &&
          <div>
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>Objectives ({objTotal})</span>
                  <div className="flex gap-2">
                    {objStatusCounts.green > 0 && <span className="text-emerald-400">{objStatusCounts.green} 🟢</span>}
                    {objStatusCounts.yellow > 0 && <span className="text-amber-400">{objStatusCounts.yellow} 🟡</span>}
                    {objStatusCounts.red > 0 && <span className="text-red-400">{objStatusCounts.red} 🔴</span>}
                  </div>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden flex">
                  {objStatusCounts.green > 0 &&
              <div className="bg-emerald-500" style={{ width: `${objStatusCounts.green / objTotal * 100}%` }} />
              }
                  {objStatusCounts.yellow > 0 &&
              <div className="bg-amber-500" style={{ width: `${objStatusCounts.yellow / objTotal * 100}%` }} />
              }
                  {objStatusCounts.red > 0 &&
              <div className="bg-red-500" style={{ width: `${objStatusCounts.red / objTotal * 100}%` }} />
              }
                </div>
              </div>
          }
            {actTotal > 0 &&
          <div>
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>Activities ({actTotal})</span>
                  <div className="flex gap-2">
                    {actStatusCounts.green > 0 && <span className="text-emerald-400">{actStatusCounts.green} 🟢</span>}
                    {actStatusCounts.yellow > 0 && <span className="text-amber-400">{actStatusCounts.yellow} 🟡</span>}
                    {actStatusCounts.red > 0 && <span className="text-red-400">{actStatusCounts.red} 🔴</span>}
                  </div>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden flex">
                  {actStatusCounts.green > 0 &&
              <div className="bg-emerald-500" style={{ width: `${actStatusCounts.green / actTotal * 100}%` }} />
              }
                  {actStatusCounts.yellow > 0 &&
              <div className="bg-amber-500" style={{ width: `${actStatusCounts.yellow / actTotal * 100}%` }} />
              }
                  {actStatusCounts.red > 0 &&
              <div className="bg-red-500" style={{ width: `${actStatusCounts.red / actTotal * 100}%` }} />
              }
                </div>
              </div>
          }
          </div>
        }

        {(mission.sponsor_email || mission.owner_email) &&
        <div className="mt-4 flex gap-4 text-xs">
            {mission.sponsor_email &&
          <div className="flex items-center gap-2">
                <span className="text-slate-500">Sponsor:</span>
                <span className="text-slate-300">{officers.find((o) => o.user_email === mission.sponsor_email)?.callsign || mission.sponsor_email}</span>
              </div>
          }
            {mission.owner_email &&
          <div className="flex items-center gap-2">
                <span className="text-slate-500">Owner:</span>
                <span className="text-slate-300">{officers.find((o) => o.user_email === mission.owner_email)?.callsign || mission.owner_email}</span>
              </div>
          }
          </div>
        }

        {(mission.commanders_intent || mission.briefing_text || linkedNodes.length > 0 || (mission.briefing_files || []).length > 0 || mission.briefing_dates) &&
        <div className="mt-4 border-t border-slate-800 pt-4">
            <button
            onClick={() => setShowBriefing(!showBriefing)}
            className="flex items-center gap-2 text-sm font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
              <FileText className="w-4 h-4" />
              Commander's Intent and Theater of War (Context Briefing)
              {showBriefing ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showBriefing &&
          <div className="mt-3 space-y-3 text-xs">
                {mission.commanders_intent &&
            <div>
                    <div className="text-slate-500 font-medium mb-2">Commander's Intent</div>
                    <p className="text-slate-400 leading-relaxed whitespace-pre-wrap">{mission.commanders_intent}</p>
                  </div>
            }

                {linkedNodes.length > 0 &&
            <div>
                    <div className="text-slate-500 font-medium mb-2">Strategic Priorities</div>
                    <div className="flex flex-wrap gap-2">
                      {linkedNodes.map((node) =>
                <Badge key={node.id} variant="outline" className="text-slate-300 border-slate-700">
                          {node.type}: {node.title}
                        </Badge>
                )}
                    </div>
                  </div>
            }

                {mission.briefing_text &&
            <div>
                    <div className="text-slate-500 font-medium mb-2">Briefing</div>
                    <p className="text-slate-400 leading-relaxed whitespace-pre-wrap">{mission.briefing_text}</p>
                  </div>
            }

                {mission.briefing_events && mission.briefing_events.length > 0 &&
            <div>
                    <div className="text-slate-500 font-medium mb-2 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Key Dates & Events
                    </div>
                    <div className="space-y-1">
                      {mission.briefing_events.map((event, idx) => {
                        const eventColors = {
                          milestone: 'bg-purple-600 text-white border-purple-500',
                          deadline: 'bg-red-600 text-white border-red-500',
                          review: 'bg-blue-600 text-white border-blue-500',
                          checkpoint: 'bg-amber-600 text-white border-amber-500',
                          launch: 'bg-emerald-600 text-white border-emerald-500',
                          recurring: 'bg-cyan-600 text-white border-cyan-500'
                        };
                        return (
                <div key={idx} className="text-slate-400 flex items-center gap-2 text-xs">
                          <Badge className={`text-[10px] ${eventColors[event.event_type] || 'bg-slate-600 text-white'}`}>{event.event_type}</Badge>
                          <span>{event.event_name}</span>
                          {event.event_date && <span className="text-slate-500">• {format(new Date(event.event_date), 'MMM d, yyyy')}</span>}
                          {event.frequency && <span className="text-slate-500">• {event.frequency}</span>}
                        </div>
                        );
                      })}
                    </div>
                  </div>
            }

                {(mission.briefing_files || []).length > 0 &&
            <div>
                    <div className="text-slate-500 font-medium mb-2">Critical Files</div>
                    <div className="space-y-1">
                      {mission.briefing_files.map((fileUrl, idx) =>
                <a
                  key={idx}
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-indigo-400 hover:text-indigo-300">
                          <FileText className="w-3 h-3" />
                          File {idx + 1}
                        </a>
                )}
                    </div>
                  </div>
            }
              </div>
          }
          </div>
        }

        <div className="mt-6 space-y-4">
          <div className="space-y-2">
            {/* Strategic Alignment */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Strategic:</span>
              <div className="flex items-center gap-1">
                {[...Array(7)].map((_, i) => {
                  const strategicCount = linkedNodes.filter((n) => n.type === 'strategy').length;
                  const isActive = i < strategicCount;
                  return (
                    <Shield
                      key={i}
                      className={`w-3.5 h-3.5 ${isActive ? 'text-indigo-400 fill-indigo-400/20' : 'text-slate-700'}`} />);


                })}
              </div>
            </div>

            {/* Tactical Alignment */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-500">Tactical:</span>
              <div className="flex items-center gap-1">
                {[...Array(3)].map((_, i) => {
                  const tacticalCount = linkedNodes.filter((n) => n.type === 'tactics').length;
                  const isActive = i < tacticalCount;
                  return (
                    <Shield
                      key={i}
                      className={`w-3.5 h-3.5 ${isActive ? 'text-cyan-400 fill-cyan-400/20' : 'text-slate-700'}`} />);


                })}
              </div>
              {linkedNodes.filter((n) => n.type === 'tactics').length > 0 &&
              <div className="flex items-center gap-1">
                  {linkedNodes.filter((n) => n.type === 'tactics').map((node) =>
                <Badge key={node.id} variant="outline" className="text-[10px] text-cyan-400 border-cyan-400/30">
                      {node.title}
                    </Badge>
                )}
                </div>
              }
            </div>
          </div>
          
          {!isCompleted && !isArchived && canCompleteMission &&
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setClosingMission(mission);
            }}
            className={`
                gap-2 
                ${isSpecialOps ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'}
              `}>

              <CheckCircle className="w-4 h-4" />
              Complete Mission
            </Button>
          }
          {isCompleted &&
          <div className="flex flex-col gap-2">
              <Badge className="bg-emerald-500/20 text-emerald-400 border-none self-start">Completed</Badge>
              {mission.outcome &&
            <div className="text-xs space-y-1">
                  <div><span className="text-slate-500">Outcome:</span> <span className="text-slate-300">{mission.outcome}</span></div>
                  {mission.evidence && <div><span className="text-slate-500">Evidence:</span> <span className="text-slate-300">{mission.evidence}</span></div>}
                </div>
            }
            </div>
          }
        </div>
      </motion.div>);

  };

  const filteredMissions = missions.
  filter((m) => {
    // Archived filter
    if (!showArchived && m.status === 'archived') return false;

    // Game mode filter
    if (activeTab !== 'all' && m.game_mode !== activeTab) return false;

    // Tactical priorities filter
    if (selectedTacticalPriorities.length > 0) {
      const missionTacticalPriorities = (m.linked_priorities || []).
      filter((id) => strategicNodes.find((n) => n.id === id && n.type === 'tactics'));
      const hasMatch = missionTacticalPriorities.some((id) => selectedTacticalPriorities.includes(id));
      if (!hasMatch) return false;
    }

    // Sponsor filter
    if (selectedSponsors.length > 0 && !selectedSponsors.includes(m.sponsor_email)) return false;

    // Owner filter
    if (selectedOwners.length > 0 && !selectedOwners.includes(m.owner_email)) return false;

    return true;
  }).
  sort((a, b) => b.xp_reward - a.xp_reward);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Mission Control</h1>
          <p className="text-slate-400">Select your theatre of operations.</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingMission(null);
              setNewMission({ title: '', objective: '', game_mode: 'rank_and_file', xp_reward: 100, sponsor_email: '', owner_email: '', affiliated_officers: [], commanders_intent: '', linked_priorities: [], briefing_text: '', briefing_files: [], briefing_events: [], status_flag: 'yellow', fibery_url: '', target_measurement_id: '', target_value: '', target_unit: '', target_position_type: '', target_position_value: '', target_deadline: '' });
            }
          }}>
            {isCommander && (
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Play className="w-4 h-4 mr-2" /> New Mission
                </Button>
              </DialogTrigger>
            )}
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingMission ? 'Edit Mission' : 'Initiate New Mission'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-6 mt-4">
              <div className="space-y-2">
                <Label>Operation Name</Label>
                <Input
                  value={newMission.title}
                  onChange={(e) => setNewMission({ ...newMission, title: e.target.value })}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                  placeholder="e.g. Operation Nightfall" />

              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Game Mode</Label>
                  <Select
                    value={newMission.game_mode}
                    onValueChange={(v) => setNewMission({ ...newMission, game_mode: v })}>

                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rank_and_file">Rank & File</SelectItem>
                      <SelectItem value="campaign">Campaign</SelectItem>
                      <SelectItem value="special_ops">Special Ops</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>XP Reward</Label>
                  <Input
                    type="number"
                    value={newMission.xp_reward}
                    onChange={(e) => setNewMission({ ...newMission, xp_reward: parseInt(e.target.value) })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500" />

                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sponsor</Label>
                  <Select
                    value={newMission.sponsor_email}
                    onValueChange={(v) => setNewMission({ ...newMission, sponsor_email: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Select sponsor" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={null}>None</SelectItem>
                      {officers.filter((officer) => officer.custom_role === 'Commander' || officer.custom_role === 'Commander in Chief').map((officer) =>
                      <SelectItem key={officer.id} value={officer.user_email}>
                          {officer.callsign} - {officer.rank_title} (L{officer.clearance_level}) • {officer.user_email}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Owner</Label>
                  <Select
                    value={newMission.owner_email}
                    onValueChange={(v) => setNewMission({ ...newMission, owner_email: v })}>
                    <SelectTrigger className="bg-slate-950 border-slate-800">
                      <SelectValue placeholder="Select owner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={null}>None</SelectItem>
                      {officers.filter((officer) => officer.custom_role === 'Commander' || officer.custom_role === 'Commander in Chief').map((officer) =>
                      <SelectItem key={officer.id} value={officer.user_email}>
                          {officer.callsign} - {officer.rank_title} (L{officer.clearance_level}) • {officer.user_email}
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Affiliated Officers</Label>
                <div className="flex flex-wrap gap-2 p-3 bg-slate-950 border border-slate-800 rounded-lg min-h-[42px]">
                  {(newMission.affiliated_officers || []).map((email) => {
                    const officer = officers.find((o) => o.user_email === email);
                    return (
                      <Badge key={email} variant="secondary" className="bg-slate-100 text-secondary-foreground px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent hover:bg-secondary/80">
                        {officer?.callsign || email}
                        <button
                          type="button"
                          onClick={() => setNewMission({
                            ...newMission,
                            affiliated_officers: newMission.affiliated_officers.filter((e) => e !== email)
                          })}
                          className="ml-1 hover:text-red-400">
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (!newMission.affiliated_officers.includes(value)) {
                      setNewMission({
                        ...newMission,
                        affiliated_officers: [...(newMission.affiliated_officers || []), value]
                      });
                    }
                  }}>
                  <SelectTrigger className="bg-slate-950 border-slate-800">
                    <SelectValue placeholder="Add affiliated officer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {officers.filter((o) =>
                      !newMission.affiliated_officers?.includes(o.user_email) &&
                      o.user_email !== newMission.sponsor_email &&
                      o.user_email !== newMission.owner_email &&
                      o.user_email
                    ).map((officer) =>
                      <SelectItem key={officer.id} value={officer.user_email}>
                        {officer.callsign} - {officer.rank_title} (L{officer.clearance_level}) • {officer.user_email}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Fibery URL (Optional)</Label>
                <Input
                  value={newMission.fibery_url}
                  onChange={(e) => setNewMission({ ...newMission, fibery_url: e.target.value })}
                  className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                  placeholder="https://yourworkspace.fibery.io/..." />
              </div>

              <div className="space-y-4 border-t border-slate-800 pt-6">
                <div className="flex items-center gap-2 text-indigo-400 mb-3">
                  <Target className="w-5 h-5" />
                  <h3 className="font-semibold">Commander's Intent</h3>
                </div>

                <div className="space-y-2">
                  <Label>Mission Objective / Strategic Intent</Label>
                  <Textarea
                    value={newMission.commanders_intent}
                    onChange={(e) => setNewMission({ ...newMission, commanders_intent: e.target.value, objective: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[100px]"
                    placeholder="High-level purpose and desired end state. What success looks like and why this mission matters..." />
                </div>

                <div className="space-y-2">
                  <Label>Strategic & Tactical Priorities</Label>
                  <div className="space-y-2">
                    {(newMission.linked_priorities || []).length > 0 &&
                    <div className="bg-slate-950 border border-slate-800 rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-900/50 border-b border-slate-800">
                            <tr>
                              <th className="text-left p-2 text-slate-400 font-medium">Type</th>
                              <th className="text-left p-2 text-slate-400 font-medium">Priority</th>
                              <th className="w-16 p-2"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(newMission.linked_priorities || []).map((priorityId) => {
                            const node = strategicNodes.find((n) => n.id === priorityId);
                            if (!node) return null;
                            return (
                              <tr key={priorityId} className="border-b border-slate-800 last:border-0">
                                  <td className="p-2 text-slate-400 capitalize">{node.type}</td>
                                  <td className="p-2 text-slate-300">{node.title}</td>
                                  <td className="p-2 text-right">
                                    <Button
                                    type="button"
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => removePriority(priorityId)}
                                    className="h-7 w-7 text-slate-500 hover:text-rose-400">
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </td>
                                </tr>);

                          })}
                          </tbody>
                        </table>
                      </div>
                    }
                    <div className="flex gap-2">
                      <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                        <SelectTrigger className="bg-slate-950 border-slate-800 flex-1">
                          <SelectValue placeholder="Select a priority..." />
                        </SelectTrigger>
                        <SelectContent>
                          {strategicNodes.length === 0 ?
                          <div className="p-2 text-sm text-slate-500">No priorities available</div> :

                          ['strategy', 'tactics'].map((type) => {
                            const nodes = strategicNodes.filter((n) => n.type === type);
                            if (nodes.length === 0) return null;
                            return (
                              <div key={type}>
                                  <div className="px-2 py-1.5 text-xs text-slate-500 uppercase font-semibold">{type}</div>
                                  {nodes.map((node) =>
                                <SelectItem
                                  key={node.id}
                                  value={node.id}
                                  disabled={(newMission.linked_priorities || []).includes(node.id)}>
                                      {node.title}
                                    </SelectItem>
                                )}
                                </div>);

                          })
                          }
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        onClick={addPriority}
                        disabled={!selectedPriority}
                        className="bg-indigo-600 hover:bg-indigo-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 border-t border-slate-800 pt-6">
                <div className="flex items-center gap-2 text-amber-400 mb-3">
                  <Target className="w-5 h-5" />
                  <h3 className="font-semibold">Bullseye Target</h3>
                  <span className="text-xs text-slate-500 ml-auto">Structured measurement for the chain rule</span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-amber-400">Measurement</Label>
                    <MeasurementCombobox
                      value={newMission.target_measurement_id}
                      onChange={(vocabId, selected) => {
                        setNewMission({
                          ...newMission,
                          target_measurement_id: vocabId,
                          target_unit: selected?.unit || '',
                        });
                      }}
                      measurements={measurements}
                      categories={measurementCategories}
                      allowedTiers={TIER_FILTERS.mission}
                      className="bg-slate-950 border-slate-800 h-10 w-full text-xs"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2">
                      <Label className="text-xs text-amber-400">Target Value</Label>
                      <FormattedNumberInput
                        placeholder="e.g. 12"
                        value={newMission.target_value}
                        onChange={(v) => setNewMission({ ...newMission, target_value: v })}
                        className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-amber-400">Unit</Label>
                      <Input
                        value={newMission.target_unit}
                        onChange={(e) => setNewMission({ ...newMission, target_unit: e.target.value })}
                        placeholder="auto-filled"
                        className="bg-slate-950 border-slate-800 text-slate-400 placeholder:text-slate-600 text-sm" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs text-amber-400">Position Scope</Label>
                    <PositionCombobox
                      value={newMission.target_position_type}
                      onChange={(v) => setNewMission({ ...newMission, target_position_type: v })}
                      positions={positions}
                      categories={positionCategories}
                      className="bg-slate-950 border-slate-800 h-10 w-full text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-amber-400">Position Value</Label>
                    <PositionValueCombobox
                      value={newMission.target_position_value}
                      onChange={(v) => setNewMission({ ...newMission, target_position_value: v })}
                      suggestions={positionSuggestions}
                      placeholder="Type or search..."
                      className="bg-slate-950 border-slate-800 h-10 w-full text-xs"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-amber-400">Target Deadline</Label>
                  <Input
                    type="date"
                    value={newMission.target_deadline}
                    onChange={(e) => setNewMission({ ...newMission, target_deadline: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 max-w-xs" />
                </div>
              </div>

              <div className="space-y-4 border-t border-slate-800 pt-6">
                <div className="flex items-center gap-2 text-indigo-400 mb-3">
                  <FileText className="w-5 h-5" />
                  <h3 className="font-semibold">Theater of War (Context Briefing)</h3>
                </div>

                <div className="space-y-2">
                  <Label>Briefing Context</Label>
                  <Textarea
                    value={newMission.briefing_text}
                    onChange={(e) => setNewMission({ ...newMission, briefing_text: e.target.value })}
                    className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[100px]"
                    placeholder="Mission context, background, objectives, constraints..." />
                </div>

                <div className="space-y-2">
                  <Label>Key Dates & Events</Label>
                  {(newMission.briefing_events || []).length > 0 &&
                  <div className="space-y-2 mb-3">
                      {newMission.briefing_events.map((event, idx) => {
                      const eventColors = {
                        milestone: 'bg-purple-600 text-white border-purple-500',
                        deadline: 'bg-red-600 text-white border-red-500',
                        review: 'bg-blue-600 text-white border-blue-500',
                        checkpoint: 'bg-amber-600 text-white border-amber-500',
                        launch: 'bg-emerald-600 text-white border-emerald-500',
                        recurring: 'bg-cyan-600 text-white border-cyan-500'
                      };
                      return (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-950 border border-slate-800 rounded">
                          <Badge className={`text-xs ${eventColors[event.event_type] || 'bg-slate-600 text-white'}`}>{event.event_type}</Badge>
                          <span className="text-sm text-slate-300 flex-1">{event.event_name}</span>
                          <span className="text-xs text-slate-500">{format(new Date(event.event_date), 'MMM d')}</span>
                          {event.frequency && <span className="text-xs text-slate-500">• {event.frequency}</span>}
                          <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => removeEvent(idx)}
                        className="h-7 text-slate-500 hover:text-rose-400">

                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                    </div>
                  }
                  <div className="grid grid-cols-2 gap-2">
                    <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="milestone">Milestone</SelectItem>
                        <SelectItem value="deadline">Deadline</SelectItem>
                        <SelectItem value="review">Review</SelectItem>
                        <SelectItem value="checkpoint">Checkpoint</SelectItem>
                        <SelectItem value="launch">Launch</SelectItem>
                        <SelectItem value="recurring">Recurring</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Event name"
                      value={newEvent.event_name}
                      onChange={(e) => setNewEvent({ ...newEvent, event_name: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500" />

                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={newEvent.event_date}
                      onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500" />

                    {newEvent.event_type === 'recurring' &&
                    <Select value={newEvent.frequency} onValueChange={(v) => setNewEvent({ ...newEvent, frequency: v })}>
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue placeholder="Frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="as_needed">As needed</SelectItem>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="per_shift">Per Shift</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="biweekly">Biweekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    }
                  </div>
                  <Button
                    type="button"
                    onClick={addEvent}
                    disabled={!newEvent.event_name || !newEvent.event_date}
                    className="w-full bg-slate-800 hover:bg-slate-700">

                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Critical Files</Label>
                  <div className="space-y-2">
                    {(newMission.briefing_files || []).map((fileUrl, idx) =>
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-950 border border-slate-800 rounded">
                        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          File {idx + 1}
                        </a>
                        <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveFile(fileUrl)}
                        className="text-slate-500 hover:text-rose-400 h-7">
                          Remove
                        </Button>
                      </div>
                    )}
                    <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-800 rounded-lg hover:border-indigo-500/50 cursor-pointer transition-colors">
                      <Upload className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-400">
                        {uploadingFile ? 'Uploading...' : 'Upload File'}
                      </span>
                      <input
                        type="file"
                        onChange={handleFileUpload}
                        disabled={uploadingFile}
                        className="hidden" />
                    </label>
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                {editingMission ? 'Update Mission' : 'Deploy Mission'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GameModeCard
          mode="rank_and_file"
          title="Rank & File"
          icon={Shield}
          count={getMissionsByMode('rank_and_file').length}
          color={{ bg: 'bg-slate-500', text: 'text-white' }}
          description="Standard operating procedures. Routine maintenance and daily tasks." />

        <GameModeCard
          mode="campaign"
          title="Campaigns"
          icon={Target}
          count={getMissionsByMode('campaign').length}
          color={{ bg: 'bg-indigo-500', text: 'text-white' }}
          description="Long-term strategic initiatives. High impact, multi-stage objectives." />

        <GameModeCard
          mode="special_ops"
          title="Special Ops"
          icon={Zap}
          count={getMissionsByMode('special_ops').length}
          color={{ bg: 'bg-rose-500', text: 'text-white' }}
          description="Critical, time-sensitive interventions. High risk, maximum reward." />

      </div>

      <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="flex justify-between items-center">
          <TabsList className="bg-slate-900 border border-slate-800">
            <TabsTrigger value="all">All Sectors</TabsTrigger>
            <TabsTrigger value="rank_and_file">Rank & File</TabsTrigger>
            <TabsTrigger value="campaign">Campaigns</TabsTrigger>
            <TabsTrigger value="special_ops">Special Ops</TabsTrigger>
          </TabsList>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowArchived(!showArchived)}
            className={`border-slate-700 ${showArchived ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'text-slate-400'}`}>

            <Archive className="w-4 h-4 mr-2" />
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
        </div>

        {/* Priority Filters */}
        <div className="space-y-4 p-4 bg-slate-900/50 rounded-lg border border-slate-800">
          {/* Tactical Priorities */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-400">Tactical Priorities</Label>
            <div className="flex flex-wrap gap-2">
              {strategicNodes.filter((n) => n.type === 'tactics').map((node) =>
              <Badge
                key={node.id}
                variant={selectedTacticalPriorities.includes(node.id) ? "default" : "outline"}
                className={`cursor-pointer transition-colors ${
                selectedTacticalPriorities.includes(node.id) ?
                'bg-cyan-600 text-white border-none' :
                'border-slate-700 text-slate-400 hover:bg-slate-800'}`
                }
                onClick={() => {
                  setSelectedTacticalPriorities((prev) =>
                  prev.includes(node.id) ?
                  prev.filter((id) => id !== node.id) :
                  [...prev, node.id]
                  );
                }}>

                  {node.title}
                </Badge>
              )}
              {strategicNodes.filter((n) => n.type === 'tactics').length === 0 &&
              <span className="text-xs text-slate-600">No tactical priorities defined</span>
              }
            </div>
          </div>

          {/* Sponsor and Owner Filters */}
          <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
            {/* Sponsor Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Filter by Sponsor</Label>
              <div className="flex flex-wrap gap-2">
                {officers.map((officer) =>
                <Badge
                  key={officer.id}
                  variant={selectedSponsors.includes(officer.user_email) ? "default" : "outline"}
                  className={`cursor-pointer transition-colors ${
                  selectedSponsors.includes(officer.user_email) ?
                  'bg-purple-600 text-white border-none' :
                  'border-slate-700 text-slate-400 hover:bg-slate-800'}`
                  }
                  onClick={() => {
                    setSelectedSponsors((prev) =>
                    prev.includes(officer.user_email) ?
                    prev.filter((email) => email !== officer.user_email) :
                    [...prev, officer.user_email]
                    );
                  }}>

                    {officer.callsign}
                  </Badge>
                )}
              </div>
            </div>

            {/* Owner Filter */}
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Filter by Owner</Label>
              <div className="flex flex-wrap gap-2">
                {officers.map((officer) =>
                <Badge
                  key={officer.id}
                  variant={selectedOwners.includes(officer.user_email) ? "default" : "outline"}
                  className={`cursor-pointer transition-colors ${
                  selectedOwners.includes(officer.user_email) ?
                  'bg-emerald-600 text-white border-none' :
                  'border-slate-700 text-slate-400 hover:bg-slate-800'}`
                  }
                  onClick={() => {
                    setSelectedOwners((prev) =>
                    prev.includes(officer.user_email) ?
                    prev.filter((email) => email !== officer.user_email) :
                    [...prev, officer.user_email]
                    );
                  }}>

                    {officer.callsign}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Clear Filters */}
          {(selectedTacticalPriorities.length > 0 || selectedSponsors.length > 0 || selectedOwners.length > 0) &&
          <div className="pt-4 border-t border-slate-800">
              <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelectedTacticalPriorities([]);
                setSelectedSponsors([]);
                setSelectedOwners([]);
              }}
              className="text-slate-400 hover:text-white">

                Clear All Filters
              </Button>
            </div>
          }
        </div>

        <TabsContent value={activeTab} className="mt-0">
          {activeTab === 'all' ? (
            // Group by game mode when "All Sectors" is selected
            <div className="space-y-8">
              {['rank_and_file', 'campaign', 'special_ops'].map((gameMode) => {
                const modeMissions = filteredMissions.filter(m => m.game_mode === gameMode);
                if (modeMissions.length === 0) return null;
                
                const modeTitle = gameMode === 'rank_and_file' ? 'Rank & File' :
                                  gameMode === 'campaign' ? 'Campaigns' : 'Special Ops';
                const modeColor = gameMode === 'rank_and_file' ? 'text-slate-400' :
                                  gameMode === 'campaign' ? 'text-indigo-400' : 'text-rose-400';
                
                return (
                  <div key={gameMode} className="space-y-4">
                    <h2 className={`text-2xl font-bold ${modeColor} uppercase tracking-wider flex items-center gap-2`}>
                      {gameMode === 'rank_and_file' && <Shield className="w-6 h-6" />}
                      {gameMode === 'campaign' && <Target className="w-6 h-6" />}
                      {gameMode === 'special_ops' && <Zap className="w-6 h-6" />}
                      {modeTitle}
                    </h2>
                    <div className="space-y-4">
                      <AnimatePresence mode="popLayout">
                        {modeMissions.map((mission) => (
                          <MissionCard key={mission.id} mission={mission} />
                        ))}
                      </AnimatePresence>
                    </div>
                  </div>
                );
              })}
              {filteredMissions.length === 0 &&
                <div className="py-20 text-center text-slate-500 border border-dashed border-slate-800 rounded-xl">
                  No active directives in this sector.
                </div>
              }
            </div>
          ) : (
            // Single game mode view
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {filteredMissions.map((mission) => (
                  <MissionCard key={mission.id} mission={mission} />
                ))}
              </AnimatePresence>
              {filteredMissions.length === 0 &&
                <div className="py-20 text-center text-slate-500 border border-dashed border-slate-800 rounded-xl">
                  No active directives in this sector.
                </div>
              }
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ArchiveWarningDialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        onConfirm={handleArchiveConfirm}
        entityType="Mission"
        entityName={missionToArchive?.title}
        dependents={archiveDependents} />


      <MissionClosureDialog
        mission={closingMission}
        open={!!closingMission}
        onClose={() => setClosingMission(null)}
        user={user} />

    </div>);

}